import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, Send, LifeBuoy, Shield, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';
import SEOHead from '@/components/SEOHead';
import { useAuth } from '@/context/AuthContext';
import { extractErrorMessage } from '@/lib/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  open:          { label: 'Open',        className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  in_progress:   { label: 'In progress', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  resolved:      { label: 'Resolved',    className: 'bg-blue-100 text-blue-700 border-blue-200' },
  closed:        { label: 'Closed',      className: 'bg-slate-200 text-slate-600 border-slate-300' },
};

const CATEGORY_LABELS = {
  bug: 'Bug',
  billing: 'Billing',
  feature_request: 'Feature request',
  account: 'Account',
  other: 'Other',
};

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso || ''; }
};

/** Single-ticket thread + reply box.
 *  Used by both user and admin — the admin flavour renders an extra status
 *  selector and uses the admin reply endpoint. The ticket itself is loaded
 *  from `/api/support/tickets/{id}` which already enforces owner-or-admin
 *  access on the server, so we don't double-gate on the client. */
export default function SupportTicketDetail({ adminMode = false }) {
  const { ticketId } = useParams();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages?.length]);

  const fetchTicket = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/support/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(extractErrorMessage(data, 'Could not load ticket'));
        return;
      }
      setTicket(data.ticket);
    } finally {
      setLoading(false);
    }
  };

  const sendReply = async () => {
    if (reply.trim().length < 1) return;
    setSending(true);
    try {
      const token = localStorage.getItem('session_token');
      const endpoint = adminMode
        ? `/api/admin/support/tickets/${ticketId}/reply`
        : `/api/support/tickets/${ticketId}/reply`;
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ body: reply }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(extractErrorMessage(data, 'Could not send reply'));
        return;
      }
      setTicket(data.ticket);
      setReply('');
      toast.success('Reply sent');
    } finally {
      setSending(false);
    }
  };

  const updateStatus = async (status) => {
    setSavingStatus(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(
        `${API_URL}/api/admin/support/tickets/${ticketId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ status }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(extractErrorMessage(data, 'Could not update status'));
        return;
      }
      setTicket(data.ticket);
      toast.success(`Status set to ${status.replace('_', ' ')}`);
    } finally {
      setSavingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!ticket) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Ticket not found.</p>
          <Link to={adminMode ? '/admin' : '/support'} className="text-[#00247D] text-sm font-semibold mt-2 inline-block">
            Back
          </Link>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  const canReply = ticket.status !== 'closed' || adminMode;

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead title={`${ticket.subject} | Support | Schedule Guru`} description="Support ticket thread." path={`/support/${ticket.id}`} />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to={adminMode ? '/admin' : '/support'}>
          <Button variant="ghost" size="sm" className="mb-4" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </Link>

        <div className="flex items-start justify-between mb-4 gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[#00247D] mb-1">
              <LifeBuoy className="w-4 h-4" />
              <span className="text-[11px] uppercase tracking-widest font-semibold">Ticket #{ticket.id.slice(0, 8)}</span>
            </div>
            <h1 className="text-2xl font-black text-slate-900 break-words" data-testid="ticket-subject">{ticket.subject}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge variant="outline" className={`${meta.className} text-[11px]`} data-testid="ticket-status">
                {meta.label}
              </Badge>
              <span className="text-[11px] text-slate-500">{CATEGORY_LABELS[ticket.category] || ticket.category}</span>
              <span className="text-[11px] text-slate-400">· opened {fmtDate(ticket.created_at)}</span>
            </div>
          </div>
          {adminMode && (
            <div className="flex items-center gap-1 flex-wrap">
              {['open', 'in_progress', 'resolved', 'closed'].map((s) => (
                <Button
                  key={s}
                  variant={ticket.status === s ? 'default' : 'outline'}
                  size="sm"
                  className={ticket.status === s ? 'bg-[#00247D]' : ''}
                  onClick={() => updateStatus(s)}
                  disabled={savingStatus || ticket.status === s}
                  data-testid={`admin-status-${s}`}
                >
                  {STATUS_META[s].label}
                </Button>
              ))}
            </div>
          )}
        </div>

        {adminMode && ticket.context && (
          <Card className="mb-4 bg-slate-50 border-slate-200">
            <CardContent className="p-3 text-[11px] text-slate-600 space-y-0.5">
              <div><strong>User:</strong> {ticket.user_name || '—'} ({ticket.user_email})</div>
              {ticket.context.page_url && <div><strong>From page:</strong> <span className="break-all">{ticket.context.page_url}</span></div>}
              {ticket.context.user_agent && <div><strong>Browser:</strong> {ticket.context.user_agent}</div>}
              {ticket.context.app_version && <div><strong>App version:</strong> {ticket.context.app_version}</div>}
            </CardContent>
          </Card>
        )}

        <div className="space-y-3 mb-6" data-testid="ticket-messages">
          {(ticket.messages || []).map((m) => {
            const isAdminMsg = m.author_type === 'admin';
            return (
              <Card key={m.id} className={isAdminMsg ? 'border-[#00247D]/30 bg-blue-50/30' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2 text-xs">
                    {isAdminMsg
                      ? <Shield className="w-3.5 h-3.5 text-[#00247D]" />
                      : <UserIcon className="w-3.5 h-3.5 text-slate-500" />}
                    <span className="font-semibold text-slate-900">{isAdminMsg ? 'Schedule Guru Support' : m.author_name}</span>
                    <span className="text-slate-400">· {fmtDate(m.created_at)}</span>
                  </div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap break-words" data-testid={`msg-body-${m.id}`}>
                    {m.body}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {canReply ? (
          <Card>
            <CardContent className="p-4">
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={adminMode ? 'Reply to the customer…' : 'Add a reply…'}
                rows={4}
                maxLength={5000}
                data-testid="reply-input"
              />
              <div className="flex justify-end mt-3">
                <Button
                  onClick={sendReply}
                  disabled={sending || reply.trim().length === 0}
                  className="bg-[#00247D] hover:bg-[#001A5C] text-white"
                  data-testid="reply-send-btn"
                >
                  {sending
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                    : <><Send className="w-4 h-4 mr-2" /> Send reply</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-slate-100">
            <CardContent className="p-4 text-center text-sm text-slate-500">
              This ticket is closed. <Link to="/support/new" className="text-[#00247D] font-semibold hover:underline">Open a new one</Link> if you need more help.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
